import type { NextRequest } from 'next/server';
import { ONETRUST_CATEGORIES, type OneTrustCategory } from './index';

/**
 * Server-side OneTrust consent helpers.
 *
 * The client-side dispatcher in `./index.ts` reads `window.OnetrustActiveGroups`;
 * here we mirror the same semantics against the request: parse the
 * `OptanonConsent` cookie, fall back to the `Sec-GPC: 1` request header when
 * the cookie is absent (so GPC opt-outs are respected before OneTrust hydrates),
 * and default to opt-in otherwise.
 */

export const OPTANON_CONSENT_COOKIE = 'OptanonConsent';

/**
 * Parse the OptanonConsent cookie's `groups=` segment and return the list of
 * categories the user has consented to (entries marked `:1`).
 *
 * The cookie body looks like
 *   `isGpcEnabled=0&datestamp=...&groups=C0001%3A1%2CC0002%3A0%2CC0003%3A1`
 * which decodes to `C0001:1,C0002:0,C0003:1`. Returns only the categories
 * with `:1`. Empty/malformed inputs return an empty array.
 */
function parseOptanonConsentGroups(cookieValue: string | undefined): string[] {
  if (!cookieValue) return [];
  const parts = cookieValue.split('&');
  for (const part of parts) {
    if (!part.startsWith('groups=')) continue;
    try {
      const decoded = decodeURIComponent(part.slice('groups='.length));
      return decoded
        .split(',')
        .map(entry => {
          const [category, consent] = entry.split(':');
          return consent?.trim() === '1' ? category.trim() : '';
        })
        .filter(Boolean);
    } catch {
      return [];
    }
  }
  return [];
}

function gpcEnabled(secGpcHeader: string | null | undefined): boolean {
  return secGpcHeader?.trim() === '1';
}

/**
 * Returns true when the user has consented to `category`.
 *
 * Rules:
 *   - OptanonConsent cookie present + category granted -> true
 *   - OptanonConsent cookie present + category denied  -> false
 *   - OptanonConsent cookie absent + Sec-GPC: 1        -> false (GPC opt-out
 *     before OneTrust hydrates)
 *   - OptanonConsent cookie absent + no GPC             -> true (default opt-in)
 *
 * Strictly Necessary (C0001) is always granted.
 */
export function hasConsentFromHeaders(
  optanonConsentCookie: string | undefined,
  secGpcHeader: string | null | undefined,
  category: OneTrustCategory,
): boolean {
  if (category === ONETRUST_CATEGORIES.STRICTLY_NECESSARY) return true;

  if (optanonConsentCookie) {
    return parseOptanonConsentGroups(optanonConsentCookie).includes(category);
  }

  return !gpcEnabled(secGpcHeader);
}

/**
 * Convenience for route handlers / server libs that already hold a NextRequest.
 */
export function hasConsentFromRequest(request: NextRequest, category: OneTrustCategory): boolean {
  return hasConsentFromHeaders(
    request.cookies.get(OPTANON_CONSENT_COOKIE)?.value,
    request.headers.get('sec-gpc'),
    category,
  );
}
